/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { CardSkills, ProductCard, breakpoints } from '@coursera/cds-core';
import type {
  ProductCard as ProductCardTrackingData,
  Product as ProductTrackingData,
  ProductType,
  Recommender,
} from '@coursera/event-pulse-types';
import type { EventData } from '@coursera/event-pulse/react';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { PathwayContentBadge } from 'bundles/product-card/components/Badges';
import LearningProductCardBadge, { getCreditType } from 'bundles/product-card/components/LearningProductCardBadge';
import LearningProductCardFooter from 'bundles/product-card/components/LearningProductCardFooter';
import { useStatusTagRedesignExperimentVariant } from 'bundles/product-card/components/utils/getStatusTagRedesignExperimentVariant';
import {
  capitalizeAndTranslateEachWordInEntityType,
  eventingV3ProductType,
  extractEventId,
} from 'bundles/product-card/components/utils/productCardV2Utils';
import useOnVisible from 'bundles/product-card/hooks/useOnVisible';
import type { LEARNING_PRODUCTS } from 'bundles/search-common/constants';
import { getUpsellCopy } from 'bundles/stackability-common/stackability-common-index';

const styles = {
  leftAlignProductCardTags: css`
    ${breakpoints.up('md')} {
      .cds-ProductCard-listCard {
        .cds-ProductCard-statusTags {
          position: absolute;
          left: 0;
          right: unset;
          top: var(--cds-spacing-100);
          padding-left: var(--cds-spacing-100);
          max-width: 298px;
          z-index: 1;
        }
      }
    }
  `,

  rightAlignProductCardTags: css`
    ${breakpoints.up('sm')} {
      .cds-ProductCard-statusTagsOverlay {
        left: unset !important;
        right: 0;
      }
    }
  `,

  cardContainer: css`
    min-height: 200px;
    height: 100%;
    display: flex;
    flex-direction: column;
    position: relative;
    cursor: pointer;

    .cds-ProductCard-partnerLogos img {
      vertical-align: unset;
    }

    .cds-CommonCard-previewImage {
      position: relative;
    }
  `,

  listCardContainer: css`
    /* intentionally unset min-height for list card that was default from CDS */
    min-height: unset;

    .cds-ProductCard-base.cds-ProductCard-list {
      height: inherit;

      /* intentionally unset min-width for list card that was default from CDS */
      min-width: unset;
    }
  `,

  experimentalStyles: css`
    & div.cds-ProductCard-content div.cds-ProductCard-footer {
      justify-content: flex-end;
    }
  `,
};

type Tag = string | React.ReactElement;
type LearningProduct = (typeof LEARNING_PRODUCTS)[keyof typeof LEARNING_PRODUCTS];

type Props = {
  adminContent?: React.ReactNode;
  customMetadata?: string[];
  customOnClick?: () => void;
  ImageComponent?: React.ReactNode;
  name: string;
  linkProps?: { href?: string; target?: string; rel?: string; component?: React.ElementType };
  titleComponent?: 'h3' | 'h4';
  partners?: { id: string; logo?: string; name: string }[];
  skills?: string[];
  bodyContent?: React.ReactNode;
  numProductRatings?: number;
  avgProductRating?: number;
  productType?: LearningProduct;
  productDifficultyLevel?: string;
  productDuration?: string;
  statusTags?: { tags: Tag[]; tagNames?: string[] };
  preBadgeContent?: React.ReactNode;
  badgeConfig?: {
    hideCreditBadge?: boolean;
    productType?: LearningProduct;
    isPathwayContent?: boolean;
  };
  statusTagConfig?: {
    hideCreditStatusTag?: boolean;
    hideAllStatusTags?: boolean;
  };
  postBadgeContent?: React.ReactNode;
  variant?: 'list' | 'grid';
  size?: 'compact' | 'standard';
  id?: string;
  slug?: string;
  cardIndex?: number;
  indexName?: string;
  recommender?: Recommender;
  trackingProps?: {
    trackedLinkProps?: {
      trackingName: string;
      withVisibilityTracking: boolean;
      requireFullyVisible: boolean;
      data: Record<string, unknown>;
      refAlt?: React.RefObject<HTMLAnchorElement>;
    };
    additionalProductCardTrackingData?: Partial<ProductCardTrackingData>;
    additionalProductTrackingData?: Partial<ProductTrackingData>;
    additionalV3TrackingData?: Record<string, unknown>;
  };
  isMetadataAboveProductRating?: boolean;
  buttons?: Array<React.ReactElement>;
  subscriptionFooterTag?: string;
  /** Pass-through to LearningProductCardFooter — renders badge after metadata, just above buttons. */
  badgeAfterMetadata?: boolean;
};

const LearningProductCard = ({
  adminContent,
  customMetadata,
  customOnClick,
  ImageComponent,
  name,
  titleComponent,
  linkProps,
  partners,
  skills,
  bodyContent,
  numProductRatings,
  avgProductRating,
  productType,
  productDifficultyLevel,
  productDuration,
  statusTags,
  preBadgeContent,
  badgeConfig,
  statusTagConfig,
  postBadgeContent,
  variant = 'grid',
  size = 'standard',
  id,
  slug,
  cardIndex,
  indexName,
  recommender,
  trackingProps,
  isMetadataAboveProductRating = false,
  buttons,
  subscriptionFooterTag,
  badgeAfterMetadata = false,
}: Props) => {
  const track = useTracker();
  const getTrackingProductId = (productId: string | undefined): string => {
    if (typeof productId !== 'string') {
      return '';
    }
    if (productId.startsWith('child~')) {
      return productId; // Preserve "child~" prefix for product IDs in tracking events
    }
    return extractEventId(productId) || '';
  };

  const {
    statusTag: experimentalTag,
    logImpressionIfShould,
    statusTagRedesignVariant,
    productCardExperimentVariant,
  } = useStatusTagRedesignExperimentVariant(slug);
  const displayStatusTagRedesign = statusTagRedesignVariant !== 'control';

  const cardMetadata =
    customMetadata ||
    [productDifficultyLevel, capitalizeAndTranslateEachWordInEntityType(productType), productDuration].filter(Boolean);

  // Rendering list cards with even a single status tag can cause styling issues with longer-typed
  // languages for desktop widths(see https://coursera.atlassian.net/browse/MERCH-1660). As a
  // workaround, we left-align tags and assign a max-width slightly less than the preview image.
  const listCardWithStatusTags = variant === 'list' && statusTags?.tags?.length && statusTags?.tags?.length >= 1;

  const getStatusTags = (): { tags: Tag[]; tagNames?: string[] } | undefined => {
    if (statusTagConfig?.hideAllStatusTags) {
      return undefined;
    }

    if (displayStatusTagRedesign) {
      return undefined;
    }

    if (experimentalTag) {
      return {
        tags: [experimentalTag.tag],
        tagNames: [experimentalTag.tagName],
      };
    }

    return statusTags;
  };

  const combinedBadge = (
    <>
      {preBadgeContent}
      {!displayStatusTagRedesign && !badgeConfig?.hideCreditBadge && (
        <LearningProductCardBadge
          customBadge={badgeConfig?.isPathwayContent ? <PathwayContentBadge /> : undefined}
          productType={badgeConfig?.productType}
        />
      )}
      {postBadgeContent}
    </>
  );

  const creditBadgePillTag = !statusTagConfig?.hideCreditStatusTag && getCreditType(badgeConfig?.productType).label;

  const getStatusTagsForFooter = () => {
    if (!displayStatusTagRedesign || statusTagConfig?.hideAllStatusTags) {
      return undefined;
    }

    let tagNames = statusTags?.tagNames ?? [];

    if (experimentalTag) {
      // We use experimentalTag.tag instead of .tagName here, because `tag` is already a translated string
      tagNames = [experimentalTag.tag];
    } else if (badgeConfig?.isPathwayContent) {
      tagNames = [...tagNames, getUpsellCopy().homepagesNavXdp.buildTowardADegree];
    } else if (creditBadgePillTag) {
      tagNames = [...tagNames, creditBadgePillTag];
    }

    if (subscriptionFooterTag) {
      tagNames = [subscriptionFooterTag, ...tagNames];
    }

    return tagNames;
  };

  const trackingData: EventData<'click_product_card'> = {
    product: {
      id: getTrackingProductId(id),
      name,
      slug: slug ?? linkProps?.href ?? '',
      type: eventingV3ProductType(productType) as ProductType,
      ...trackingProps?.additionalProductTrackingData,
    },
    productCard: {
      ...(typeof trackingProps?.trackedLinkProps?.data.productCard === 'object'
        ? trackingProps?.trackedLinkProps?.data.productCard
        : {}),
      index: cardIndex ?? 0,
      ...(indexName && { algoliaIndexName: indexName }),
      ...(recommender && { recommender }),
      tags: displayStatusTagRedesign ? getStatusTagsForFooter() : getStatusTags()?.tagNames,
      ...(trackingProps?.additionalProductCardTrackingData?.pageConfigComponent && {
        pageConfigComponent: trackingProps?.additionalProductCardTrackingData?.pageConfigComponent,
      }),
    },
    ...trackingProps?.additionalV3TrackingData,
  };

  const trackedCardRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_product_card',
    trackingData,
    [],
    { fullyVisible: true, initialDelay: 1000 }
  );

  useOnVisible(trackedCardRef, logImpressionIfShould, { fullyVisible: true, initialDelay: 1000, once: true });

  return (
    <div
      css={[
        styles.cardContainer,
        listCardWithStatusTags ? styles.leftAlignProductCardTags : styles.rightAlignProductCardTags,
        variant === 'list' ? styles.listCardContainer : null,
        displayStatusTagRedesign && styles.experimentalStyles,
      ]}
      data-testid="product-card-cds"
    >
      <ProductCard
        experimentVariant={productCardExperimentVariant}
        onClick={(event) => {
          track('click_product_card', trackingData);
          if (linkProps?.href) {
            return;
          }
          if (customOnClick) {
            event.preventDefault();
            event.stopPropagation();
            customOnClick();
          }
        }}
        ref={trackedCardRef}
        renderPreviewImage={() => ImageComponent}
        title={{
          name,
          linkProps,
          component: titleComponent,
          customLinkProps: trackingProps?.trackedLinkProps,
        }}
        partners={partners}
        body={
          (skills && skills.length > 0) || bodyContent ? (
            <>
              {skills && skills.length > 0 && <CardSkills skills={skills} />}
              {bodyContent}
            </>
          ) : undefined
        }
        footer={
          <LearningProductCardFooter
            isMetadataAboveProductRating={isMetadataAboveProductRating}
            cardMetadata={cardMetadata}
            avgProductRating={avgProductRating}
            numProductRatings={numProductRatings}
            badge={combinedBadge}
            shouldUseColorCodedPillTags={displayStatusTagRedesign}
            statusTagNames={getStatusTagsForFooter()}
            buttons={buttons}
            productCardVariant={variant}
            badgeAfterMetadata={badgeAfterMetadata}
          />
        }
        variant={variant}
        size={size}
        productType={productType || ''}
        statusTags={getStatusTags()?.tags}
        adminContent={adminContent}
      />
    </div>
  );
};

export default LearningProductCard;
